import type { HttpErrorResponse } from '@angular/common/http';

import type { MemoizedSelector } from '@ngrx/store';
import { createSelector, createSelectorFactory, defaultMemoize } from '@ngrx/store';
import stringify from 'fast-json-stable-stringify';

import { UserSelectors } from '@core/account-data';
import { CandidateSelectors } from '@core/candidate-data';
import type { Candidate } from '@core/candidate-types';
import type {
  MailboxConvertData,
  MailboxMessage,
  MailboxRecipient,
  MailboxSearchParams,
  MailboxThread,
} from '@core/mailbox-types';
import type { CorrelationId, MetaScopeOptions } from '@core/ngrx-legacy';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  pipeState,
} from '@core/ngrx-legacy';
import { JobsBriefSelectors } from '@core/offers-data';
import type { Visibility } from '@core/visibility-types';

import type { MailboxThreadsCounters, MailboxThreadUpdate } from './mailbox-threads-http-client';

export const threadCandidateScope = (params: { candidateId: number }) =>
  `candidate-${stringify(params)}`;
export const threadScope = (params: Partial<MailboxSearchParams>) => {
  const { page, limit, ...temp } = params;
  return `search-${stringify(temp)}`;
};

type MailboxMeta = { scopeMeta: { counters: { total: number; unread: number } } };

const decorateMessages =
  (state: any) =>
  (messages: MailboxMessage[]): MailboxMessage[] => {
    const messageRecipientWithRefs = fillMessageRecipientRefs(state);

    return (messages || []).map(m => ({
      ...m,
      from: Array.isArray(m.from) ? m.from.map(messageRecipientWithRefs) : [],
      to: Array.isArray(m.to) ? m.to.map(messageRecipientWithRefs) : [],
      cc: Array.isArray(m.cc) ? m.cc.map(messageRecipientWithRefs) : [],
      bcc: Array.isArray(m.bcc) ? m.bcc.map(messageRecipientWithRefs) : [],
    }));
  };

const fillMessageRecipientRefs =
  (state: any) =>
  (recipient: MailboxRecipient): MailboxRecipient => ({
    ...recipient,
    candidate: recipient.candidateId
      ? (CandidateSelectors.byId(recipient.candidateId!)(state) ?? undefined)
      : undefined,
    admin: recipient.adminId
      ? (UserSelectors.byId(recipient.adminId!)(state) ?? undefined)
      : undefined,
  });

const relatedThreadsSelector = memoizeSelector(
  (threadIds: number[]): MemoizedSelector<any, any> =>
    createSelectorFactory(defaultMemoize)(
      (state: any) => state,
      ...threadIds.map(threadId => selectors.byId(threadId)),
      (state: any, ...result: MailboxThread[]) =>
        result
          .filter(t => t)
          .map(thread => ({
            ...thread,
            messages: decorateMessages(state)(thread.messages),
          })),
    ),
);

const { actions, reducer, namespace, selectors } = createEntityCollectionStore<
  MailboxThread,
  MailboxMeta
>()('mailbox-threads', {
  references: makeRef => [
    makeRef('relatedIds', 'related', (threadIds: MailboxThread['relatedIds']) =>
      relatedThreadsSelector(threadIds || <number[]>[]),
    ),
    makeRef(
      'candidateId',
      'candidate',
      candidateId => CandidateSelectors.byIdWithReferences(candidateId!)!,
    ),
    makeRef('offerId', 'offer', offerId => JobsBriefSelectors.byId(offerId!)!),
    makeRef('messages', 'messages', messages => state => decorateMessages(state)(messages)),
  ],
  // eslint-disable-next-line max-lines-per-function
  customActions: adapter => {
    const selectId = ({ id }: MailboxThread) => id;
    const defaultActions = getEntityCollectionActions(adapter, selectId);

    return {
      fetchCollection: (
        state,
        payload: { params: Partial<MailboxSearchParams>; scope: string; clearScope?: boolean },
      ) => defaultActions.fetchCollection(state, payload),
      fetchCollectionSuccess: (
        state,
        payload: { data: MailboxThreadsCounters; scope: string; clearScope: boolean },
      ) =>
        pipeState(
          state,
          next => (payload.clearScope ? adapter.resetScope(next, payload.scope) : next),
          next =>
            adapter.setScopeMetaCustom(
              next,
              { counters: payload.data.counters },
              { scope: payload.scope },
            ),
          next => adapter.upsertMany(next, payload.data.threads, { scope: payload.scope }),
          next => adapter.markAsLoaded(next, { scope: payload.scope, entities: [] }),
        ),
      fetchCollectionFailed: (state, payload) =>
        defaultActions.fetchCollectionFailed(state, payload),

      fetchOne: (state, payload: { id: number }) => defaultActions.fetchOne(state, payload),
      fetchOneSuccess: (state, payload: { data: MailboxThread }) =>
        pipeState(
          state,
          next => adapter.upsertOne(next, payload.data),
          next =>
            adapter.markAsLoaded(next, {
              entities: [payload.data.id],
              scope: null,
            }),
        ),
      fetchOneFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
        defaultActions.fetchOneFailed(state, payload),

      fetchCandidateCollection: (state, payload: { candidateId: number; scope: string }) =>
        defaultActions.fetchCollection(state, payload),
      fetchCandidateCollectionSuccess: defaultActions.fetchCollectionSuccess,
      fetchCandidateCollectionFailed: defaultActions.fetchCollectionFailed,

      update: (
        state,
        payload: { id: number; params: { candidateId?: number; visibility?: Visibility } },
      ) => adapter.markAsPending(state, { entities: [payload.id], scope: null }),
      updateSuccess: (state, payload: MailboxThreadUpdate) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload),
          next => adapter.markAsLoaded(next, { entities: [payload.id], scope: null }),
        ),
      updateFailed: (state, payload) =>
        adapter.setError(state, payload.error, { entities: [payload.id], scope: null }),

      delete: (state, payload: { id: number }) =>
        adapter.markAsPending(state, { entities: [payload.id], scope: null }),
      deleteSuccess: (state, payload: { id: number }) => adapter.removeOne(state, payload.id),
      deleteFailed: (state, payload: { id: number; error: HttpErrorResponse }) =>
        adapter.setError(state, payload.error, { entities: [payload.id], scope: null }),

      markRead: (state, payload: { id: number; read: boolean }) =>
        adapter.markAsPending(state, { entities: [payload.id], scope: null }),
      markReadSuccess: (state, payload: { id: number; read: boolean }) =>
        pipeState(
          state,
          next => adapter.updateOne(next, payload),
          next => adapter.markAsLoaded(next, { entities: [payload.id], scope: null }),
        ),
      markReadFailed: (state, payload) =>
        adapter.setError(state, payload.error, { entities: [payload.id], scope: null }),

      markAllAsRead: ngrxEvent<{
        searchParams: Partial<MailboxSearchParams>;
        until: string | undefined;
      }>(),
      markAllAsReadSuccess: ngrxEvent<void>(),
      markAllAsReadFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

      markArchived: (state, payload: { id: number; archived: boolean }) =>
        adapter.markAsPending(state, { entities: [payload.id], scope: null }),
      markArchivedSuccess: (state, payload: { id: number; archived: boolean }) =>
        adapter.removeOne(state, payload.id),
      markArchivedFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

      convert: ngrxEvent<{ data: MailboxConvertData } & CorrelationId>(),
      convertSuccess: ngrxEvent<{ data: Candidate } & CorrelationId>(),
      convertFailed: ngrxEvent<{ error: HttpErrorResponse } & CorrelationId>(),

      resyncEmailsConversation: ngrxEvent<{ candidateId: number }>(),
      resyncEmailsConversationSuccess: ngrxEvent<{ candidateId: number }>(),
      resyncEmailsConversationFailed: ngrxEvent<{ error: HttpErrorResponse }>(),

      references: (state, payload: { data: MailboxThread[] }) =>
        adapter.updateOrCreateMany(state, payload.data),
    };
  },
});

type UsedActions =
  | 'fetchCollection'
  | 'fetchCollectionSuccess'
  | 'fetchCollectionFailed'
  | 'fetchOne'
  | 'fetchOneSuccess'
  | 'fetchOneFailed'
  | 'fetchCandidateCollection'
  | 'fetchCandidateCollectionSuccess'
  | 'fetchCandidateCollectionFailed'
  | 'update'
  | 'updateSuccess'
  | 'updateFailed'
  | 'delete'
  | 'deleteSuccess'
  | 'deleteFailed'
  | 'markRead'
  | 'markReadSuccess'
  | 'markReadFailed'
  | 'markAllAsRead'
  | 'markAllAsReadSuccess'
  | 'markAllAsReadFailed'
  | 'markArchived'
  | 'markArchivedSuccess'
  | 'markArchivedFailed'
  | 'convert'
  | 'convertSuccess'
  | 'convertFailed'
  | 'resyncEmailsConversation'
  | 'resyncEmailsConversationSuccess'
  | 'resyncEmailsConversationFailed'
  | 'references';

const MailboxThreadsActions = <Pick<typeof actions, UsedActions>>actions;
const MailboxThreadsSelectors = {
  ...selectors,
  entitiesSortedWithReferences: memoizeSelector((options?: MetaScopeOptions) =>
    createSelector(selectors.entitiesWithReferences(options), (threads: MailboxThread[]) =>
      [...threads].sort((a, b) => (a.lastMessageAt > b.lastMessageAt ? -1 : 1)),
    ),
  ),
};

export {
  MailboxThreadsActions,
  actions,
  MailboxThreadsSelectors,
  reducer as MailboxThreadsReducer,
  namespace as MailboxThreadsNamespace,
};
